<template>
  <v-app>
    <v-snackbar
      v-model="snackbar.show"
      :timeout="snackbar.timeout"
      :color="snackbar.color"
      top
      right
    >
      <pre>{{ snackbar.message }}</pre>
    </v-snackbar>
    <v-toolbar v-if="user.orgName" color="white" app fixed class="elevation-1 pl-0 app-global-toolbar" height="60" style="z-index: 10">
      <v-toolbar-title class="ml-0 pl-2 fsz-16 fsz-md-30">
        <span id="app-logo" @click="goToPage('/home')">{{ hospitalName }}</span>
        <!-- 版本號小字：顯示在 org 名稱右側，沿用 SignIn.vue 的 packageInfo 來源 -->
        <span class="app-version grey--text">v.{{ version }}</span>
      </v-toolbar-title>
      <v-toolbar-items class="v-toolbar-items">
        <v-spacer />
  
        <v-layout align-center justify-end>
          <v-flex class="d-flex flex-grow-0">
            <!-- 全站暗色主題切換（KAN-95 之後搬到全域 header，登入後每頁可切）：
                 內視鏡室暗環境減少白底炫光；沿用 Vuex root action toggleUiTheme -->
            <v-btn
              icon
              data-test="theme-toggle"
              :title="isDarkTheme ? $t('theme.switch_to_light') : $t('theme.switch_to_dark')"
              @click="toggleTheme"
            >
              <v-icon class="fsz-16 fsz-md-24">{{ isDarkTheme ? 'brightness_7' : 'brightness_4' }}</v-icon>
            </v-btn>
            <v-menu offset-y left>
              <v-btn slot="activator" icon>
                <v-icon class="fsz-16 fsz-md-24">settings</v-icon>
              </v-btn>
              <v-list class="overflow-y-auto menu-height">
                <v-list-tile
                  class="menu-background"
                  v-for="(item, index) in adminPages"
                  :key="index"
                  @click="goToPage(`/admin/${item.href}`)"
                >
                  <v-list-tile-title>{{ item.name }}</v-list-tile-title>
                </v-list-tile>
                <v-list-tile class="menu-background" key="test" @click="goToPage('/test')">
                  <v-list-tile-title>{{ $t('enviroment_test.title') }}</v-list-tile-title>
                </v-list-tile>
              </v-list>
            </v-menu>
            <!-- 介面語言切換。title 顯示目前語系——選單收起來時，地球 icon
                 本身看不出現在是哪一國語言。 -->
            <v-menu offset-y left>
              <v-btn slot="activator" icon data-test="locale-toggle" :title="$t(`i18n.${currentLocale}`)">
                <v-icon class="fsz-16 fsz-md-24">language</v-icon>
              </v-btn>
              <v-list class="overflow-y-auto menu-height">
                <v-list-tile
                  class="menu-background"
                  v-for="locale in locales"
                  :key="locale"
                  :data-test="`locale-${locale}`"
                  @click="on_change_locale(locale)"
                >
                  <v-list-tile-title :class="{ 'primary--text font-weight-bold': locale === currentLocale }">
                    {{ $t(`i18n.${locale}`) }}
                  </v-list-tile-title>
                  <v-list-tile-action v-if="locale === currentLocale">
                    <v-icon class="fsz-16" color="primary">check</v-icon>
                  </v-list-tile-action>
                </v-list-tile>
              </v-list>
            </v-menu>
          </v-flex>
          <v-flex class="flex-grow-0 d-none d-md-block">
            <v-btn flat v-if="user">
              {{ user.username || user.name }}
              <v-icon :color="voiceRepeatColor" class="pl-1">
                {{ voiceRepeatIcon }}
              </v-icon>
            </v-btn>
          </v-flex>
          <v-flex class="flex-grow-0">
            <v-btn @click="logout" flat class="fsz-12 fsz-md-14">{{ $t('homepage.signout') }}</v-btn>
          </v-flex>
        </v-layout>
      </v-toolbar-items>
    </v-toolbar>
    <v-content class="app__content">
      <v-container fluid fill-height class="pa-0">
        <router-view></router-view>
      </v-container>
    </v-content>

    <div
      v-if="reportFinalizationActive"
      ref="reportFinalizationOverlay"
      class="report-finalization-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="報告處理中"
      aria-live="polite"
      aria-busy="true"
      tabindex="0"
      data-test="report-finalization-overlay"
    >
      <div class="report-finalization-overlay__wrap">
        <v-progress-circular indeterminate color="primary" :size="58" :width="5" />
        <h1>{{ reportFinalizationMessage }}</h1>
        <p>已等待 {{ reportFinalizationElapsed }}</p>
        <small>處理完成後會自動開放報告覆核。</small>
      </div>
    </div>
    <v-alert
      v-else-if="reportFinalizationTerminal.status && reportFinalizationTerminal.status !== 'ready'"
      class="report-finalization-notice"
      :value="true"
      type="warning"
      outline
      dismissible
      role="alert"
      data-test="report-finalization-terminal"
    >
      {{ reportFinalizationTerminal.message }}
    </v-alert>
    <div class="loading-overlay" v-show="getLoading && !reportFinalizationActive">
      <div class="loading-overlay__wrap">
        <v-progress-circular indeterminate color="primary" id="loading-indicator" />
        <h2 class="loading-overlay__typing" :style="styled(getLoadingDescript.length)" v-if="getLoadingDescript">
          {{getLoadingDescript}}
        </h2>
      </div>
    </div>
    <v-dialog v-if="editDialog" v-model="editDialog" persistent max-width="500px">
      <v-card>
        <v-card-text>
          <v-container>
            <div>
              <div cols="6">
                <span>院內碼:</span>
                <span>{{ examInfo.hospitalCode || '' }}</span>
              </div>
            </div>
            <div>
              <div cols="6">
                <span>日期:</span>
                <span>{{ examInfo.examinationDate | Date }}</span>
              </div>
            </div>
            <div>
              <div cols="6">
                <span>病患姓名:</span>
                <span>{{ examInfo.patientName }}</span>
              </div>
            </div>
            <div>
              <div cols="6">
                <span>病歷號碼:</span>
                <span>{{ examInfo.medicalRecord }}</span>
              </div>
            </div>
            <div>
              <div cols="6">
                <span>檢查單號:</span>
                <span>{{ examInfo.examinationRecord }}</span>
              </div>
            </div>
            <div align="center">
              <div cols="6">
                <v-text-field v-model="newAccNo" filled label="新檢查單號" clearable></v-text-field>
              </div>
            </div>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn color="darken-2" text @click="editDialog = false"> Cancel </v-btn>

          <v-btn color="darken-2" text @click="reportShippingToNewAccNo()"> Send </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-if="transferLoading" v-model="transferLoading" hide-overlay persistent width="300">
      <v-card color="primary" dark>
        <v-card-text>
          報告轉送中
          <v-progress-linear indeterminate color="white" class="mb-0"></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog v-if="notifyDialog" v-model="notifyDialog" max-width="290">
      <v-card>
        <v-card-title class="text-h3"> 報告轉送結果 </v-card-title>

        <v-card-text>
          {{ transferResultMessage }}
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn color="darken-1" text @click="initialNotifyMessage()"> 知道了 </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <notifications position="top center" group="topCenter">
      <template slot="body" slot-scope="{ item, close }">
        <div class="d-flex justify-between align-center pl-4 flex-grow-0 alert-container">
          <p class="alert-container__content text-sm-center">{{ item.text }}</p>
          <v-btn
            class="flex-grow-0 white--text"
            type="button"
            flat
            fab
            small
            @click="close"
          >
          <v-icon> close </v-icon>
        </v-btn>
        </div>
      </template>
    </notifications> 
  </v-app>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex'
import { reportServices } from '@/services/db'
import axios from 'axios'
import { get } from 'lodash'
import packageInfo from '../package.json'
import { DEFAULT_LOCALE, SUPPORTED_LOCALES } from '@/utils/locale'

export default {
  computed: {
    ...mapGetters(['getLoading', 'getUser', 'getLoadingDescript', 'getUiTheme', 'getHospital']),
    ...mapGetters('reportFinalization', {
      reportFinalizationActive: 'active',
      reportFinalizationStartedAtMs: 'startedAtMs',
      reportFinalizationMessage: 'stageMessage',
      reportFinalizationTerminal: 'terminal',
    }),
    // 全站暗色主題目前是否為暗色（供切換鈕 icon / tooltip 顯示）
    isDarkTheme () {
      return this.getUiTheme === 'dark'
    },
    ...mapState('snackBar', ['show', 'message', 'color', 'timeout']),
    snackbar() {
      return {
        show: this.show,
        message: this.message,
        color: this.color,
        timeout: this.timeout
      }
    },
    user() {
      return (
        this.getUser || {
          username: '',
          name: '',
          employee_typeID: 1,
          employeeID: 0,
          orgNo: '',
          orgName: '',
          useGuiding: 0,
          level: '1',
        }
      )
    },
    hospitalName () {
      return (this.getHospital && this.getHospital.display_name) || this.user.orgName
    },
    accNo() {
      return Number(this.$route.params.accNo) != 0
        ? this.$route.params.accNo
        : 0
    },
    examId() {
      return this.$route.params.examinationID || -1
    },
    voiceRepeatIcon() {
      return Number(this.user.useGuiding) ? 'record_voice_over' : 'voice_over_off'
    },
    voiceRepeatColor() {
      return Number(this.user.useGuiding) ? 'success' : 'error'
    },
    adminPages() {
      return this.user ? this.pages.filter((p) => p.level <= this.user.level) : this.pages
    },
    styled () {
      return (leng) => {
        return {
          width: `${leng * 1.7}ch`,
          animation: `typing 2s steps(${leng}), blink 0.5s step-end infinite alternate`
        }
      }
    },
    pages() {
      return [
        {
          name: this.$t('organization.title'),
          href: 'organization-list',
          level: 1,
        },
        {
          name: this.$t('doctor.title'),
          href: 'doctor-list',
          level: 1,
        },
        {
          name: 'Employee Types',
          href: 'employeeType-list',
          level: 9,
        },
        {
          name: this.$t('examination.title'),
          href: 'examination-list',
          level: 1,
        },
        {
          name: 'Examination Types',
          href: 'examinationType-list',
          level: 9,
        },
        {
          name: 'Landmarks',
          href: 'landmark-list',
          level: 9,
        },
        {
          name: this.$t('inspection_instrument.title'),
          href: 'inspectionInstrument-list',
          level: 1,
        },
        {
          name: this.$t('wash_Instrument.title'),
          href: 'washInstrument-list',
          level: 1,
        },
        {
          name: 'Antiplatelet',
          href: 'antiplatelet-list',
          level: 9,
        },
        {
          name: 'Antispasmodic',
          href: 'antispasmodic-list',
          level: 9,
        },
        {
          name: 'Preparation Agents',
          href: 'cleanMed-list',
          level: 9,
        },
        {
          name: 'Sedative',
          href: 'sedative-list',
          level: 9,
        },
        {
          name: this.$t('patient.title'),
          href: 'patient-list',
          level: 1,
        },
        {
          name: this.$t('nurse.title'),
          href: 'technicalStaff-list',
          level: 1,
        },
        {
          name: this.$t('user.title'),
          href: 'user-list',
          level: 1,
        },
        {
          name: this.$t('analytics.title'),
          href: 'analytics',
          level: 1,
        },
      ]
    },
    /* 選單順序用 SUPPORTED_LOCALES 而不是 $i18n.availableLocales：後者的順序
       取決於 locale/index.js 裡 messages 物件的 key 順序（en, zh, ja），
       會讓預設語系的繁體中文排在英文後面。 */
    locales() {
      return SUPPORTED_LOCALES
    },

    currentLocale() {
      return this.$i18n ? this.$i18n.locale : DEFAULT_LOCALE
    },

    reportFinalizationElapsed() {
      const elapsed = Math.max(0, this.finalizationNowMs - Number(this.reportFinalizationStartedAtMs || 0))
      const totalSeconds = Math.floor(elapsed / 1000)
      const minutes = Math.floor(totalSeconds / 60)
      const seconds = String(totalSeconds % 60).padStart(2, '0')
      return `${minutes}:${seconds}`
    }
  },
  data() {
    return {
      // header 顯示用的應用版本號（取自 client/package.json）
      version: packageInfo.version,
      finalizationNowMs: Date.now(),
      finalizationClock: null,
      reportFinalizationInitialized: false,
      dialog: false,
      newAccNo: '',
      loading5: false,
      loading6: false,
      transferLoading: false,
      notifyDialog: false,
      drawer: null,
      showToolBar: true,
      editDialog: false,
      timerEnabled: false,
      isTranNewAccVisible: false,
      items: [
        { icon: 'list_alt', text: 'Examinations', href: '/examinations' },
        { icon: 'assignment', text: 'Reports', href: '/reports' },
        { icon: 'style', text: 'Admin', href: '/admin' },
      ],

      examInfo: {
        medicalRecord: '',
        examinationRecord: '',
        hospitalCode: '',
      },
      adminPagesByUser: [],
    }
  },

  methods: {
    ...mapActions(['logout']),

    // 全站暗色主題切換（root action：更新 state + 套 root class + 持久化）
    toggleTheme () {
      this.$store.dispatch('toggleUiTheme')
    },
    goToPage(page) {
      this.$router.push(page)
    },
    setToolBar() {
      this.showToolBar = this.$router.currentRoute.path.indexOf('/exam2/') < 0
    },
    async reportShippingToNewAccNo() {
      this.editDialog = false
      this.transferResultMessage = ''
      if (this.newAccNo.trim() !== '') {
        this.transferLoading = true
        const res = await reportServices.transferToNewAccNo({
          patientId: this.examInfo.medicalRecord,
          accNo: this.examInfo.examinationRecord,
          newAccNo: this.newAccNo,
        })
        this.transferResultMessage = get(res, 'data.message', '') || ''
        this.transferLoading = false
        this.transferLoading = false
        this.notifyDialog = true
      }
    },
    initialNotifyMessage() {
      this.notifyDialog = false
      this.newAccNo = ''
    },
    async getExamination(id) {
      this.selectedExamination = id || ''
      const list = (await this.getExaminations) || []
      const row = list.find((item) => item.url === id)
      if (!row) return
      this.examInfo.examinationRecord = row.accNo
      this.examInfo.medicalRecord = row.medicalRecord
      this.examInfo.examinationDate = row.date
      this.examInfo.patientName = row.patientName
      this.examInfo.hospitalCode = row.hospitalCode
    },
    handleAddAccClick() {
      this.editDialog = true
    },
    on_change_locale(locale) {
      if (this.$root) {
        this.$root.set_locale(locale)
      }
    },
    handleReportFinalizationKeydown(event) {
      if (!this.reportFinalizationActive) return
      const reload = event.key === 'F5'
        || ((event.ctrlKey || event.metaKey) && String(event.key).toLowerCase() === 'r')
      if (reload) return
      event.preventDefault()
      event.stopImmediatePropagation()
      if (this.$refs.reportFinalizationOverlay) this.$refs.reportFinalizationOverlay.focus()
    },
    handleReportFinalizationFocus(event) {
      if (!this.reportFinalizationActive || !this.$refs.reportFinalizationOverlay) return
      if (this.$refs.reportFinalizationOverlay.contains(event.target)) return
      event.preventDefault()
      this.$refs.reportFinalizationOverlay.focus()
    },
    initializeReportFinalization() {
      // 初始 router 尚未 ready 時 $route 可能仍是根路徑，不能先用它恢復舊場 polling。
      this.$router.onReady(() => {
        this.$store.dispatch('reportFinalization/initialize', this.$route)
          .then(() => { this.reportFinalizationInitialized = true })
      })
    }
  },

  watch: {
    examId() {
      this.getExamination(this.examId)
    },
    $route(to) {
      this.isTranNewAccVisible = to.path.indexOf('examinations') > -1
      if (this.reportFinalizationInitialized) {
        this.$store.dispatch('reportFinalization/adoptRoute', to)
      }
    },
    reportFinalizationActive(active) {
      document.documentElement.style.overflow = active ? 'hidden' : ''
      document.body.style.overflow = active ? 'hidden' : ''
      if (active) {
        this.$nextTick(() => {
          if (this.$refs.reportFinalizationOverlay) this.$refs.reportFinalizationOverlay.focus()
        })
      }
    },
  },
  mounted() {
    this.setToolBar()
    this.getExamination(this.examId)
    this.isTranNewAccVisible = this.$route.path.indexOf('examinations') > -1
    document.oncontextmenu = function () {
      return false
    }
    window.axios = axios
    document.addEventListener('keydown', this.handleReportFinalizationKeydown, true)
    document.addEventListener('focusin', this.handleReportFinalizationFocus, true)
    this.initializeReportFinalization()
    this.finalizationClock = window.setInterval(() => {
      this.finalizationNowMs = Date.now()
    }, 1000)
  },
  beforeDestroy() {
    if (this.finalizationClock) window.clearInterval(this.finalizationClock)
    this.$store.dispatch('reportFinalization/stopPolling')
    document.removeEventListener('keydown', this.handleReportFinalizationKeydown, true)
    document.removeEventListener('focusin', this.handleReportFinalizationFocus, true)
    document.documentElement.style.overflow = ''
    document.body.style.overflow = ''
  },
}
</script>

<style lang="scss">
.v-toolbar__content {
  padding-left: 16px;
}
.v-toolbar__title {
  overflow: visible;
}

/***** TODO *****/
/***** Make overlay 100% height of the browser and do not allow scrolling *****/
/***** And make spinner align center *****/
.loading-overlay {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: rgba(#e3e3e3, 0.9);
  z-index: 9999;
  display: flex;
  padding-top: 200px;
  justify-content: center;
  overflow: hidden;
}
.report-finalization-overlay {
  position: fixed;
  inset: 0;
  z-index: 20000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  overflow: hidden;
  touch-action: none;
  background: rgba(241, 246, 251, 0.97);
}
.report-finalization-overlay__wrap {
  width: min(520px, 100%);
  padding: 36px 28px;
  text-align: center;
  color: #263f5d;
  background: #fff;
  border: 1px solid rgba(38, 63, 93, 0.14);
  border-radius: 12px;
  box-shadow: 0 14px 44px rgba(24, 47, 72, 0.18);
}
.report-finalization-overlay__wrap h1 {
  margin: 20px 0 8px;
  font-size: 1.55rem;
}
.report-finalization-overlay__wrap p {
  margin: 0 0 8px;
  font-size: 1.05rem;
  font-variant-numeric: tabular-nums;
}
.report-finalization-overlay__wrap small {
  color: #60758d;
}
.report-finalization-notice {
  position: fixed;
  top: 72px;
  right: 16px;
  z-index: 19999;
  width: min(520px, calc(100vw - 32px));
  background: #fff;
}
.loading-overlay__wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.loading-overlay__typing {
  color: #3b587d;
  font-size: 1.4rem;
  font-weight: 600;
  letter-spacing: 2px;
  margin-top: 20px;
  overflow: hidden;
  border-right: 3px solid;
  white-space: nowrap;
}
@keyframes typing {
  from {
    width: 0
  }
}
@keyframes blink {
  50% {
    border-color: transparent
  }
}

.v-toolbar--clipped {
  z-index: 6;
}

#app-logo {
  cursor: pointer;
}

/* 版本號小字：與 org 名稱同一行、垂直置中、subtle 灰字、左側留一點間距 */
.app-version {
  margin-left: 8px;
  font-size: 12px;
  font-weight: 400;
  vertical-align: middle;
  white-space: nowrap;
}

.v-toolbar__content {
  .text-with-label-component {
    margin-left: 20px;
  }
}
.v-toolbar-items {
  width: 100%;
  align-items: center;
}

.menu-height {
  max-height: 280px;
  .menu-background {
    background: #fff;
  }
}

.alert-container {
  background-color: #404040 !important;
  border-radius: 8px;
  margin: auto;
  color: #fff;
  width: 100%;
  &__content {
    letter-spacing: 1px;
  }
}
</style>
